import React, { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { buildPortOptions } from "@features/vessel-call/create/utils/portOptions.ts";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { getVesselConditions, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { VesselConditions } from "@services/vessel/types";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FORM_FIELDS } from "../../constants";
import { useClearanceTypeQuery } from "../../hooks/useClearanceTypeQuery";
import type { RequestDepartureFormData } from "../../schema/requestDepartureFormSchema";
import type { UpdateFormData } from "../DepartureRequest/DepartureRequest";

const LANG_BASE = "vessel-call.request-departure";
const TEST_ID_BASE = "request-departure-details";
const service = "bff-vessel-call";

type RequestDepartureProps = {
  formData: UseFormReturn<RequestDepartureFormData>;
  updateFormData: UpdateFormData;
  hadCancelledDepartures: boolean;
};

const RequestDepartureDetails: React.FC<RequestDepartureProps> = ({
  formData,
  updateFormData,
  hadCancelledDepartures,
}) => {
  const { id: vesselCallId } = useParams();
  const { data: fetchedClearanceType } = useClearanceTypeQuery(vesselCallId);
  const displayRequestClearanceCheckbox = fetchedClearanceType?.displayRequestClearanceCheckbox;

  const showClearanceCheckbox = Boolean(displayRequestClearanceCheckbox || hadCancelledDepartures);

  const isDraftRequired =
    fetchedClearanceType?.displayRequestClearanceCheckbox === false &&
    !fetchedClearanceType?.hasVisitedAnchorageOnly;
  const getClearanceLabel = (clearanceType: string | undefined) => {
    if (clearanceType === "PORT_CLEARANCE") {
      return hadCancelledDepartures
        ? t(`${LANG_BASE}.form-fields.requestNewPortClearance`)
        : t(`${LANG_BASE}.form-fields.requestPortClearance`);
    } else if (clearanceType === "ANCHORAGE_CLEARANCE") {
      return hadCancelledDepartures
        ? t(`${LANG_BASE}.form-fields.requestNewAnchorageClearance`)
        : t(`${LANG_BASE}.form-fields.requestAnchorageClearance`);
    }
  };

  const nextPort = formData.watch(FORM_FIELDS.NEXT_PORT);
  const label = getClearanceLabel(fetchedClearanceType?.clearanceType);

  const { data: fetchedVesselConditions = [] } = useQuery<VesselConditions[]>({
    queryKey: ["vessel-conditions"],
    queryFn: () => getVesselConditions(service) as Promise<VesselConditions[]>,
  });

  const vesselConditionOptions = useMemo(
    () =>
      fetchedVesselConditions.map((condition) => ({
        label: String(condition.conditionDesc),
        value: String(condition.conditionCode),
      })),
    [fetchedVesselConditions],
  );

  const { options: fetchedPorts = [] } = useFormattedReference<{
    portCode: string;
    portName?: string;
    countryCode?: string;
  }>(REFERENCE_DATA_TYPES.PORTS, "bff-vessel");

  const portOptions = useMemo(() => buildPortOptions(fetchedPorts), [fetchedPorts]);
  const aePorts = useMemo(
    () => fetchedPorts.filter((port) => port.countryCode === "AE"),
    [fetchedPorts, nextPort],
  );

  const displayFinalDestination = useMemo(
    () => aePorts.find((port) => port.portCode === nextPort),
    [nextPort, aePorts],
  );

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.form-sections.departure-details`)}>
        <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.MASTER_NAME>
          name={FORM_FIELDS.MASTER_NAME}
          type="text"
          label={t(`${LANG_BASE}.form-fields.masterName`)}
          testId={`${TEST_ID_BASE}-master-name`}
          onFieldChange={updateFormData}
          maxChars={100}
        />
        <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.NEXT_PORT>
          name={FORM_FIELDS.NEXT_PORT}
          type="autocomplete"
          options={portOptions}
          label={t(`${LANG_BASE}.form-fields.nextPort`)}
          testId={`${TEST_ID_BASE}-next-port`}
          onFieldChange={updateFormData}
        />
        {displayFinalDestination && (
          <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.FINAL_DESTINATION>
            name={FORM_FIELDS.FINAL_DESTINATION}
            type="autocomplete"
            options={portOptions}
            label={t(`${LANG_BASE}.form-fields.finalDestination`)}
            testId={`${TEST_ID_BASE}-final-destination`}
            onFieldChange={updateFormData}
            required={true}
          />
        )}
        <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.VESSEL_CONDITION>
          name={FORM_FIELDS.VESSEL_CONDITION}
          type="autocomplete"
          options={vesselConditionOptions}
          label={t(`${LANG_BASE}.form-fields.vesselCondition`)}
          testId={`${TEST_ID_BASE}-vessel-condition`}
          onFieldChange={updateFormData}
        />
        <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.DRAFT_FORE>
          name={FORM_FIELDS.DRAFT_FORE}
          type="number"
          label={t(`${LANG_BASE}.form-fields.draftFore`)}
          testId={`${TEST_ID_BASE}-draft-fore`}
          onFieldChange={updateFormData}
          required={isDraftRequired}
          maxDigits={5}
          decimals={3}
          canBeginWithZero={false}
        />
        <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.DRAFT_AFT>
          name={FORM_FIELDS.DRAFT_AFT}
          type="number"
          label={t(`${LANG_BASE}.form-fields.draftAft`)}
          testId={`${TEST_ID_BASE}-draft-Aft`}
          onFieldChange={updateFormData}
          required={isDraftRequired}
          maxDigits={5}
          decimals={3}
          canBeginWithZero={false}
        />

        {showClearanceCheckbox && (
          <div className="col-span-full">
            <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.REQUEST_ANCHORAGE_CLEARANCE>
              name={FORM_FIELDS.REQUEST_ANCHORAGE_CLEARANCE}
              type="checkbox"
              horizontalRadioButtons
              label={label}
              testId={`${TEST_ID_BASE}-request-anchorage-clearance`}
              onFieldChange={updateFormData}
            />
          </div>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default RequestDepartureDetails;
